//
// Copyright (c) 2021 Astute Global, Inc.
//

// @ts-ignore
import i18next from "i18n/i18n";
import {
  addListToDropdown,
  ButtonView,
  Collection,
  createLabeledDropdown,
  CssTransitionDisablerMixin,
  IconCancel,
  IconCheck,
  LabeledFieldView,
  submitHandler,
  View,
  ViewModel,
} from "ckeditor5";
import { removeCkReset } from "../../ckEditorUtil";
import ResponseCategoriesListView from "./responseCategoriesListView";
import { DEFAULT_INSERT_TYPE, INSERT_TYPES } from "../responseCategoryCommand";

import "../css/responseCategoryView.css";

class BaseResponseCategoryView extends View {
  constructor(editor, command) {
    super(editor.locale);
    this.set("insertType", command.insertType);
    this.set("responseId", null);
    this.editor = editor;
    this.command = command;
    this.responseCategoryListView = this._createResponseCategoryListView(command);
    this.insertTypeSelectView = this._createInsertTypeSelectView();
    this.okButton = this._createOkButton();
    this.cancelButton = this._createCancelButton();
    this.bind("insertType").to(command);
    this.setTemplate({
      tag: "form",
      attributes: {
        class: ["ck", "ck-responsive-form", "ck-vertical-form", "response-category-view"],
        tabindex: "-1",
      },
      children: this._createFormChildren(),
    });
  }

  get categoryId() {
    if (this.responseCategoryListView) {
      return this.responseCategoryListView.categoryId;
    }
    return null;
  }

  get categoryName() {
    if (this.responseCategoryListView) {
      return this.responseCategoryListView.categoryName;
    }
    return null;
  }

  focus() {
    this.responseCategoryListView.focus();
  }

  render() {
    super.render();
    // Remove the CKEditor reset CSS classes to better integrate with Marionette UI
    setTimeout(() => removeCkReset(this.element), 100);
    submitHandler({
      view: this,
    });
  }

  _createCancelButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.cancel"),
      icon: IconCancel,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-cancel",
      },
    });
    button.delegate("execute").to(this, "cancel");
    return button;
  }

  _createFormChildren() {
    const children = this.createCollection();

    children.add(this.responseCategoryListView);
    children.add(this.insertTypeSelectView);
    children.add(this.okButton);
    children.add(this.cancelButton);

    return children;
  }

  _createInsertTypes() {
    const command = this.command;
    const insertTypes = new Collection();
    for (const insertType of INSERT_TYPES) {
      const model = new ViewModel({
        label: i18next.t(`ckeditor.category.type.${insertType}`),
        insertType: insertType,
        isToggleable: true,
        tooltip: true,
        withText: true,
      });
      model.bind("isOn").to(command, "insertType", (value) => value == insertType);
      insertTypes.add({
        type: "button",
        model: model,
      });
    }
    return insertTypes;
  }

  _createInsertTypeSelectView() {
    const insertTypes = this._createInsertTypes();
    const insertTypeSelectView = new LabeledFieldView(this.locale, createLabeledDropdown);
    insertTypeSelectView.set({
      class: "insert-type-wrapper",
      isEmpty: false,
      label: i18next.t("ckeditor.category.typeLabel"),
    });
    const insertTypeSelect = insertTypeSelectView.fieldView;
    addListToDropdown(insertTypeSelect, insertTypes);
    insertTypeSelect.class = "insert-type";
    insertTypeSelect.buttonView.set({
      label: i18next.t(`ckeditor.category.type.${this.command.insertType}`),
      withText: true,
    });
    insertTypeSelect.buttonView.bind("label").to(this.command, "insertType", (value) => {
      for (const insertType of insertTypes) {
        if (value == insertType.model.insertType) {
          return insertType.model.label;
        }
      }
      return i18next.t(`ckeditor.category.type.${DEFAULT_INSERT_TYPE}`);
    });
    insertTypeSelect.on("execute", (evt) => {
      this.command.set("insertType", evt.source.insertType);
    });
    return insertTypeSelectView;
  }

  _createOkButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.ok"),
      icon: IconCheck,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-save",
      },
    });
    button.type = "submit";
    button.delegate("execute").to(this, "submit");
    return button;
  }

  _createResponseCategoryListView(command) {
    const view = new ResponseCategoriesListView(this.locale);
    view.bind("categoryId").to(command, "value");
    view.bind("responseId").to(this);
    return view;
  }
}

const ResponseCategoryView = CssTransitionDisablerMixin(BaseResponseCategoryView);

export default ResponseCategoryView;
